import { CreateEventForm } from "@/types";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ForwardedRef, forwardRef, useImperativeHandle, useState } from "react";
import { Calendar, MapPin, Clock, X } from "lucide-react";
import dayjs from "dayjs";
import { DialogClose } from "@radix-ui/react-dialog";
import parse from "html-react-parser";
interface IProps {
  submitForm: () => Promise<void>;
}

export interface ModalConfirmCreateEventRef {
  handleOpen: (event: CreateEventForm) => void;
}

const ModalConfirmCreateEvent = (
  { submitForm }: IProps,
  ref: ForwardedRef<ModalConfirmCreateEventRef>,
) => {
  const [open, setOpen] = useState<boolean>(false);
  const [event, setEvent] = useState<CreateEventForm | null>(null);
  const [isMore, setIsMore] = useState<boolean>(false);

  const handleOpen = (eventRemove: CreateEventForm) => {
    setOpen(true);
    setEvent(eventRemove);
  };

  useImperativeHandle(ref, () => ({
    handleOpen,
  }));

  const handleClose = () => setOpen(false);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="w-[90%] rounded-[24px] p-5 lg:w-[879px] lg:max-w-[879px] lg:px-8 lg:py-6"
        hidden
      >
        <div className="flex items-center justify-between border-b border-[#E2E8F0] pb-4">
          <DialogTitle className="w-full ">
            <span className="text-xl font-semibold text-[#1E293B]">
              Confirm Event Creation
            </span>
          </DialogTitle>

          <DialogClose>
            <X className="size-6 text-[#334155]" />
          </DialogClose>
        </div>

        <div className="mt-3 max-h-[500px] overflow-y-auto lg:mt-4">
          <p className="mb-5 text-base text-text lg:mb-8 lg:text-center">
            Please review the event details below before adding it to the
            dashboard
          </p>

          {!!event && (
            <div className="flex flex-col items-start gap-5 lg:flex-row lg:gap-8">
              <div className="h-[176px] min-w-full md:h-[164px] md:min-w-[292px]">
                <img
                  src={event.banner}
                  className="size-full rounded-[16px] object-cover"
                />
              </div>
              <div className="flex size-full min-h-[164px] flex-col justify-between">
                <div className="mb-4 space-y-2">
                  <p className="text-base font-medium text-text">
                    {event.name}
                  </p>
                  <p
                    className={`text-sm text-[#334155] ${isMore ? "line-clamp-none" : "line-clamp-3"}`}
                  >
                    {parse(event.description)}
                  </p>
                  {event.description.length > 130 && (
                    <p
                      className="cursor-pointer select-none text-sm font-medium text-[#5248E3]"
                      onClick={() => setIsMore(!isMore)}
                    >
                      {!isMore ? "Read More" : "Read Less"}
                    </p>
                  )}
                </div>
                <div className="flex flex-col flex-wrap justify-between max-md:gap-3 lg:flex-row lg:items-center">
                  <div className="flex items-center gap-1">
                    <Calendar className="size-4 text-[#334155]" />
                    <span className="text-sm text-[#334155]">
                      {dayjs(event.startTime).format("YYYY/MM/DD")} -{" "}
                      {dayjs(event.endTime).format("YYYY/MM/DD")}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <MapPin className="size-4 text-[#334155]" />
                    <span className="text-sm text-[#334155]">
                      {event.location}
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    <Clock className="size-4 text-[#334155]" />
                    <span className="text-sm text-[#334155]">
                      Created at {dayjs().format("YYYY/MM/DD")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-5 lg:mt-6">
          <button
            onClick={handleClose}
            className="h-12 flex-1 rounded-[6px] bg-button-background text-base font-medium text-text"
          >
            Cancel
          </button>
          <button
            disabled={!event}
            onClick={submitForm}
            className="h-12 flex-1 rounded-[6px] bg-text text-base font-medium text-white"
          >
            Confirm
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default forwardRef(ModalConfirmCreateEvent);
